import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Download, Upload } from 'lucide-react';
import { API } from '@/lib/api';
import axios from 'axios';

/**
 * ExportImportButtons — paire de boutons Exporter/Importer CSV pour Admin National.
 * Props:
 *   exportPath: chemin d'export (ex: '/admin/export/users.csv')
 *   importPath: chemin d'import (ex: '/admin/import/users')
 *   filenameHint: nom suggéré du fichier téléchargé (fallback navigateur)
 *   visible: bool — caché si false (pour cacher aux non-admin)
 *   onImported: callback (result) après import réussi pour recharger la liste
 */
export default function ExportImportButtons({ exportPath, importPath, filenameHint = 'export.csv', visible = true, onImported }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  if (!visible) return null;

  const handleExport = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}${exportPath}`, { responseType: 'blob', withCredentials: true });
      const blob = new Blob([data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filenameHint;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success('Export CSV téléchargé');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'export");
    } finally {
      setBusy(false);
    }
  };

  const handleImportClick = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // permet re-sélection du même fichier
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}${importPath}`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const msg = `Importé : ${data.created} créés, ${data.skipped} ignorés${data.errors?.length ? `, ${data.errors.length} erreurs` : ''}`;
      toast.success(msg);
      if (data.errors?.length) {
        console.warn('Erreurs import:', data.errors);
        toast.warning(`${data.errors.length} ligne(s) en erreur — voir console`);
      }
      onImported?.(data);
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Erreur d'import");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-2">
      <button
        data-testid="export-csv-btn"
        onClick={handleExport}
        disabled={busy}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-2 border-stone-300 text-stone-700 hover:border-[#09090B] hover:text-[#09090B] transition-colors"
      >
        <Download className="w-3.5 h-3.5" /> Exporter CSV
      </button>
      <button
        data-testid="import-csv-btn"
        onClick={handleImportClick}
        disabled={busy}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-2 border-stone-300 text-stone-700 hover:border-[#09090B] hover:text-[#09090B] transition-colors"
      >
        <Upload className="w-3.5 h-3.5" /> Importer CSV
      </button>
      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        onChange={handleFile}
        className="hidden"
        data-testid="import-csv-input"
      />
    </div>
  );
}
